import type { ReactNode } from 'react'

import {
  ACCENT_PICKER_SHOWN,
  ACCENTS,
  changeLocale,
  THEME_PREFERENCES,
  useAccentStore,
  useThemeStore,
  type Accent,
} from '@ctcells/core'
import { getLocale, getSupportedLocales, t, type MessageKey } from '@ctcells/i18n'

import { Segmented } from '../../atoms/Segmented.tsx'
import { SettingsRow, SettingsSection } from '../../layout/SettingsList.tsx'
import { hasSkin } from '../../tokens/skin.ts'

import { ThemeIcon } from './themeIcons.tsx'

import '../screens.css'
import '../resource.css'

/** 고른 칩의 체크 — 원 위에 얹는다. 색만으로 가르지 않게 테와 «함께» 선다 */
function CheckGlyph() {
  return (
    <svg
      aria-hidden="true"
      className="swatchpick__check"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2.2}
      viewBox="0 0 16 16"
    >
      <path d="M3.5 8.5l3 3 6-6.5" />
    </svg>
  )
}

/**
 * 테마색 — **색마다 독립 칩**(eric 2026-09-23 「테마색은 저렇게 토글 형태 말고, 각각의 색을 따로
 * 칩 형태로 따로 해줘」). 그래서 `Segmented`(알약 하나 안의 구획)를 쓰지 않는다.
 *
 * 🔴 **글자는 없다**(eric 2026-09-23 「텍스트는 없애고 파랑 · 검정 둥근 원만」). 종전에는 「점만 두지
 *    않는다 — 색 구분이 어려운 눈에서 점 둘이 같아 보인다」로 이름을 옆에 적었는데 eric 이 뒤집었다.
 *    대신 ⓐ 이름은 칩의 접근 이름(`aria-label` · `title`)이 지고 ⓑ 고른 것은 **강조 테 + 체크**가
 *    말한다 — 색만으로 가르지 않는다는 규약(§8)은 그대로다.
 * 🔴 묶음은 `radiogroup` 이다. 하나만 고르는 자리라 버튼 여러 개가 아니라 라디오다.
 */
function AccentChips({ accent, onChange }: { accent: Accent; onChange: (next: Accent) => void }) {
  return (
    <div aria-label={t('accent.label')} className="swatchpick" role="radiogroup">
      {ACCENTS.map((value) => {
        const name = t(`accent.${value}` as MessageKey)
        const picked = value === accent
        return (
          <button
            aria-checked={picked}
            aria-label={name}
            className="swatchpick__chip"
            key={value}
            onClick={() => onChange(value)}
            role="radio"
            title={name}
            type="button"
          >
            <span aria-hidden="true" className={`swatch swatch--${value}`} />
            {picked ? <CheckGlyph /> : null}
          </button>
        )
      })}
    </div>
  )
}

/**
 * 내 계정 › 개인 설정 — 테마 · 테마색 · 언어. (리퀴드 글라스는 항상 켬이라 줄이 없다 — eric 2026-09-22)
 *
 * 🔴 **「변경」 단추가 없다**(eric 2026-09-22 「테마에 변경 버튼 빼고 시스템·라이트·다크 선택하게 — 하나의 칩인데 세 개
 *    선택 가능하게. 테마색은 파랑·검정 둘만. 언어는 변경 빼고 한국어만 선택된 대로」).
 *    줄마다 오른쪽에 **구획 선택기**(`Segmented`) 또는 **스위치**가 바로 서고 누르는 즉시 적용된다 — 그 자리 편집을
 *    한 단계 더 줄인 것이다(§1-3). 기본값: 테마 라이트 · 테마색 파랑.
 *
 * 🔴 **테마는 여기가 제자리다.** 설정 화면이 없는 표면(모듈 패드 3종 · 태블릿 kiosk)에만 `ThemeToggle` 이 남아 있다.
 * 🔴 **테마색 줄은 스킨이 있는 표면에만 선다**(`hasSkin()`). 장비 관점 앱에는 강조 계단이 없어 눌러도 아무 일이
 *    안 난다 — 「권한 없음」과 다르다. 그 표면에 없는 기능이라 줄을 세우지 않는다.
 * 🔴 **언어는 앱이 선언한 목록만 그린다**(`getSupportedLocales` · 통합 콘솔만 한국어 + 영어 · eric 2026-11-02). 고를 것이 하나인
 *    앱(모듈 3종 · 장비 관점)은 「한국어」 하나를 고른 채로 보이기만 한다. 고르면 저장 후 **새로 고침**이다(`changeLocale` · docs/i18n.md) —
 *    `t()` 가 순수 함수라 그 자리에서 다시 그려지지 않는다.
 * 🔴 **`children` 은 이 구획의 마지막 줄**이다. eric 2026-09-23 「비밀번호를 개인 설정 아래에 추가해줘」 —
 *    비밀번호가 제 구획(제목 「비밀번호」)을 갖지 않고 여기 한 줄로 들어온다. 창·게이트·상태는 부르는 쪽이 쥔다.
 */
export function PreferenceSettings({ children }: { children?: ReactNode }) {
  const preference = useThemeStore((s) => s.preference)
  const setPreference = useThemeStore((s) => s.setPreference)
  const accent = useAccentStore((s) => s.accent)
  const setAccent = useAccentStore((s) => s.setAccent)
  const skinned = hasSkin()
  const locales = getSupportedLocales()

  return (
    <SettingsSection title={t('section.preferences')}>
      <SettingsRow
        action={
          <Segmented
            label={t('theme.label')}
            onChange={setPreference}
            /* 아이콘 셋(eric 2026-09-23). 이름은 `title` 로 — 툴팁 · 보조기술 */
            options={THEME_PREFERENCES.map((value) => ({
              value,
              label: <ThemeIcon preference={value} />,
              title: t(`theme.${value}` as MessageKey),
            }))}
            value={preference}
          />
        }
        label={t('theme.label')}
        value={null}
      />

      {/* 「테마색」 줄 — 숨김(eric 2026-10-29 「일단 숨기기로 해줘」 · 스위치 `ACCENT_PICKER_SHOWN` · 그동안 강조색은 파랑 하나) */}
      {skinned && ACCENT_PICKER_SHOWN ? (
        <SettingsRow
          action={<AccentChips accent={accent} onChange={setAccent} />}
          label={t('accent.label')}
          value={null}
        />
      ) : null}

      {/*
        ⚠ 「챔버 표시 2D/3D」 줄은 걷었다 — 2026-10-01 회의 합의 「3D 장비 뷰 → 2D」. 챔버는 늘 2D 다
          (3D 코드 · three 의존성은 같은 날 리포에서 지웠다). 되살리지 마라.
      */}

      {/* 리퀴드 글라스 줄은 없다 — 항상 켬이다(eric 2026-09-22 「리퀴드 글라스는 항상 enable 하고 설정에서 빼줘」) */}

      <SettingsRow
        action={
          <Segmented
            label={t('setting.language')}
            onChange={locales.length > 1 ? (next) => changeLocale(next) : undefined}
            options={locales.map((locale) => ({
              value: locale,
              label: t(`locale.${locale}` as MessageKey),
            }))}
            value={getLocale()}
          />
        }
        label={t('setting.language')}
        value={null}
      />

      {/* 비밀번호 — 개인 설정의 마지막 줄(eric 2026-09-23). 제 구획을 두지 않는다 */}
      {children}
    </SettingsSection>
  )
}
